import { useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AFTER_GAME_CHANGE, submitWord } from "@/api/mutations";
import { ApiError } from "@/api/client";
import { THEME_LIST as LISTA_TEMAS, themeInfo as infoTema } from "@historia/shared/themes";
import {
  HINT_PENALTY_MS,
  WORDS_PER_PERSON as PALAVRAS_POR_PESSOA,
} from "@historia/shared/validation";
import { formatarTempo } from "@/components/jogos/RondaCronometrada";
import { useOutroNome } from "@/components/jogos/useOutroNome";
import type { VisibleWordWithGuesses as PalavraComPalpites } from "@historia/shared/contracts";

const ALVO = PALAVRAS_POR_PESSOA; // cada pessoa compõe N palavras por ronda

const campo =
  "rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-3 text-tinta outline-none transition-colors placeholder:text-tinta-700/40 focus:border-ouro focus:ring-2 focus:ring-ouro/30";

export function Compor({
  rondaId,
  minhas,
}: {
  rondaId: string;
  minhas: PalavraComPalpites[];
}) {
  const queryClient = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [palavra, setPalavra] = useState("");
  const outro = useOutroNome();
  const [aGuardar, iniciar] = useTransition();

  const faltam = ALVO - minhas.length;

  function enviar(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErro(null);
    const form = e.currentTarget;
    const dados = new FormData(form);
    dados.set("ronda_id", rondaId);
    iniciar(async () => {
      try {
        await submitWord({
          roundId: rondaId,
          tema: String(dados.get("tema") ?? ""),
          palavra,
          emojis: String(dados.get("emojis") ?? ""),
        });
        form.reset();
        setPalavra("");
        for (const key of AFTER_GAME_CHANGE) {
          await queryClient.invalidateQueries({ queryKey: key });
        }
      } catch (error) {
        setErro(error instanceof ApiError ? error.message : "Não foi possível guardar.");
      }
    });
  }

  return (
    <section>
      <h2 className="mb-1 font-titulo text-2xl text-tinta-800">A minha palavra</h2>
      <p className="mb-4 text-sm text-tinta-600">
        {faltam > 0
          ? `Escolhe um tema e uma palavra, e faz a palavra com emojis o mais claros possível. É esta que ${outro} vai ter de adivinhar.`
          : `A tua palavra está pronta. ✨ Agora é esperar que ${outro} adivinhe.`}
      </p>

      {/* A que já compus (com o resultado, se já houver) */}
      {minhas.length > 0 && (
        <ul className="mb-4 flex flex-col gap-2">
          {minhas.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 rounded-xl bg-creme-50 px-4 py-3"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="shrink-0 text-2xl" aria-hidden>
                  {p.emojis}
                </span>
                {/* Tema numa linha; a palavra na linha de baixo (melhor no telemóvel) */}
                <span className="flex min-w-0 flex-col">
                  <span className="text-xs text-tinta-500">
                    {infoTema(p.tema).icon} {infoTema(p.tema).label}
                  </span>
                  <span className="truncate font-semibold text-tinta-800">
                    {p.palavra}
                  </span>
                </span>
              </span>
              <Resultado palavra={p} />
            </li>
          ))}
        </ul>
      )}

      {faltam > 0 && (
        <form onSubmit={enviar} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta-700">Tema</span>
            <select name="tema" defaultValue="" required className={campo}>
              <option value="" disabled>
                Escolhe um tema…
              </option>
              {LISTA_TEMAS.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.icon} {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta-700">
              Palavra (a resposta — só tu a vês)
            </span>
            {/* Sempre em maiúsculas: é assim que ela a vai ver, letra a letra,
                nos traços — e tira do caminho toda uma classe de enganos de
                maiúsculas/minúsculas. */}
            <input
              name="palavra"
              required
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={palavra}
              onChange={(e) => setPalavra(e.target.value.toUpperCase())}
              placeholder="EX.: TITANIC"
              className={`${campo} uppercase`}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta-700">
              Em emojis (a pista)
            </span>
            <input
              name="emojis"
              required
              placeholder="ex.: 🚢🧊💔"
              className={`${campo} text-2xl`}
            />
          </label>

          {erro && <p className="text-sm text-rose-600">{erro}</p>}

          <button
            type="submit"
            disabled={aGuardar}
            className="self-start rounded-full bg-ouro px-5 py-2.5 font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:opacity-60"
          >
            {aGuardar ? "A guardar…" : "Guardar palavra"}
          </button>
        </form>
      )}
    </section>
  );
}

function Resultado({ palavra }: { palavra: PalavraComPalpites }) {
  if (palavra.acertou_na) {
    const total = (palavra.tempo_ms ?? 0) + palavra.pistas * HINT_PENALTY_MS;
    return (
      <span className="shrink-0 text-sm font-semibold text-emerald-600">
        {formatarTempo(total)} 🎉
      </span>
    );
  }
  if (palavra.revelada) {
    return <span className="shrink-0 text-sm text-tinta-500">Não acertou — revelada</span>;
  }
  const tentativas = palavra.palpites.length;
  return (
    <span className="shrink-0 text-sm text-tinta-500">
      {tentativas > 0
        ? `${tentativas} ${tentativas === 1 ? "tentativa" : "tentativas"}`
        : "à espera"}
    </span>
  );
}
